import { Component, OnInit } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { MatchesService } from '../../services/matches.service';
import { SidenavService } from '../../services/sidenav.service';
import { SnackbarService } from '../../services/snackbar.service';
import { TeamsService } from '../../services/teams.service';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-champion-and-pichichi',
  templateUrl: './champion-and-pichichi.component.html',
  styleUrls: ['./champion-and-pichichi.component.scss'],
  standalone: false,
})
export class ChampionAndPichichiComponent implements OnInit {
  @BlockUI() blockUI: NgBlockUI;

  sidenav: MatSidenav;
  user: any;
  userId: string;
  loggedUser: any;
  isAdmin: boolean;
  currentTimestamp: number;
  firstRoundIniTimestamp: number;
  teams: any[];
  isMe: boolean = false;
  isLockedForViewer: boolean = false;

  pichichiTeamId: string | null = null;
  pichichiPlayers: any[] = [];
  pichichiPlayersLoading: boolean = false;

  private rounds: any[];

  constructor(
    private sidenavService: SidenavService,
    private usersService: UsersService,
    private route: ActivatedRoute,
    private router: Router,
    private snackbar: SnackbarService,
    private authService: AuthService,
    private matchesService: MatchesService,
    private teamsService: TeamsService,
    private apiService: ApiService,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
    this.loggedUser = this.authService.getUser();
    this.currentTimestamp = new Date().getTime() / 1000;
  }

  async ngOnInit() {
    if (!this.loggedUser?._id) {
      this.router.navigate(['/login']);
      return;
    }

    this.rounds = await this.matchesService.getRounds();
    this.teams = await this.teamsService.getList();
    this.firstRoundIniTimestamp = this.rounds[0].iniTimestamp;
    this.userId = this.route.snapshot.params['id'];
    this.isAdmin = this.authService.isAdmin();

    if (!this.userId) {
      this.userId = this.loggedUser._id;
    }

    if (
      this.userId !== this.loggedUser._id &&
      !this.isAdmin &&
      this.currentTimestamp < this.firstRoundIniTimestamp
    ) {
      this.isLockedForViewer = true;
      this.user = await this.usersService.get(this.userId);
      return;
    }

    this.user = await this.usersService.get(this.userId);
    this.isMe = this.user._id === this.loggedUser._id;
    if (this.isMe) {
      await this.authService.updateCurrentUser(this.user);
      this.loggedUser = this.user;
    }

    if (!this.user.jokers) {
      this.user.jokers = { champion: false, pichichi: false, matches: [] };
    }
    this.user.jokers.champion = false;
    this.user.jokers.pichichi = false;

    if (!this.user.champion?._id) {
      this.user.champion = { _id: null };
    }

    await this.loadPichichiSelection();
  }

  toggleMenu() {
    this.sidenavService.toggle();
  }

  getSelectedChampionFlag(): string | null {
    if (!this.user?.champion?._id || !this.teams) return null;
    return (
      this.teams.find((t) => t._id === this.user.champion._id)?.flag ?? null
    );
  }

  getPichichiTeamFlag(): string | null {
    if (!this.pichichiTeamId || !this.teams) return null;
    return this.teams.find((t) => t._id === this.pichichiTeamId)?.flag ?? null;
  }

  getSelectedPlayerName(): string | null {
    if (!this.user?.pichichi || !this.pichichiPlayers.length) return null;
    return (
      this.pichichiPlayers.find((p) => p._id === this.user.pichichi)?.name ??
      null
    );
  }

  async onPichichiTeamChange(teamId: string) {
    this.pichichiTeamId = teamId;
    this.user.pichichi = null;
    this.pichichiPlayersLoading = true;
    try {
      this.pichichiPlayers = teamId
        ? await this.apiService.request(`/teams/${teamId}/players`, 'GET')
        : [];
    } catch {
      this.pichichiPlayers = [];
    } finally {
      this.pichichiPlayersLoading = false;
    }
  }

  update() {
    const payload = {
      ...this.user,
      champion: this.user.champion?._id ? this.user.champion : null,
      jokers: {
        ...(this.user.jokers ?? {}),
        champion: false,
        pichichi: false,
      },
    };
    this.blockUI.start();
    this.usersService
      .update(payload)
      .then(async () => {
        if (this.user._id === this.loggedUser._id) {
          const updatedUser = await this.usersService.get(this.user._id);
          await this.authService.updateCurrentUser(updatedUser);
          this.loggedUser = updatedUser;
          this.user = updatedUser;
          await this.loadPichichiSelection();
        }
        this.blockUI.stop();
        this.snackbar.success('Los datos se han actualizado correctamente.');
      })
      .catch((error) => {
        this.blockUI.stop();
        this.snackbar.error(error.message);
      });
  }

  private async loadPichichiSelection(): Promise<void> {
    const pichichiId = this.getPichichiId(this.user?.pichichi);
    this.user.pichichi = pichichiId;
    this.pichichiTeamId = null;
    this.pichichiPlayers = [];

    if (!pichichiId) {
      return;
    }

    try {
      const player: any = await this.apiService.request(
        `/players/${pichichiId}`,
        'GET',
      );
      const teamId = player?.team?._id ?? player?.team ?? player?.teamId ?? null;
      if (teamId) {
        this.pichichiTeamId = teamId;
        this.pichichiPlayers = await this.apiService.request(
          `/teams/${teamId}/players`,
          'GET',
        );
      }
    } catch {
      // pichichi player not found — leave selectors empty
    }
  }

  private getPichichiId(pichichi: any): string | null {
    if (!pichichi) {
      return null;
    }

    if (typeof pichichi === 'string') {
      return pichichi;
    }

    return pichichi._id ?? null;
  }
}
